import dayjs from 'dayjs'
import SubscriptionAPIs from '@/API/subscription/subscription-api'
import { FeatureModel } from '@/models/subscription/FeatureModel'

export default {
  namespaced: true,
  state: {
    subscription: {},
    detailsFetched: false,
    packagePermissions: {},
  },
  getters: {
    // subscription type
    subscriptionType: (state: any) => state.subscription.type,
    isPaid: (state: any) => state.subscription.type === 'paid',
    isFree: (state: any) => state.subscription.type === 'free',
    isTrial: (state: any) => state.subscription.type === 'trial',
    isCustom: (state: any) => state.subscription.type === 'custom',
    isQr: (state: any) => state.subscription.type === 'qr',
    isCustomPeriod: (state: any) => state.subscription.is_custom,
    isMonthlyPeriod: (state: any) => state.subscription.is_monthly,
    canAccessFeature: (state: any, getters: any, rootState: any, rootGetters: any) => (featureSlug: string) => !getters.isCustom || rootGetters['permissions/isSuperAdmin'] || (state.packagePermissions[featureSlug] ?? true),

    isYearlyPeriod: (state: any, getters: any) => !getters.isCustomPeriod && !getters.isMonthlyPeriod,
    isRecurringSubscriptionPayment: (state: any) => state.subscription.payment_type === 1,
    isFreeSubscriptionPayment: (state: any) => state.subscription.payment_type === 2,
    isDirectSubscriptionPayment: (state: any) => state.subscription.payment_type === 0,
    nonFreeSubscription: (state: any, getter: any) => getter.isPaid || getter.isCustom || getter.isQr,
    freeSubscription: (state: any, getter: any) => !getter.nonFreeSubscription,
    // package
    currentPackage: (state: any) => state.subscription.package ?? {},
    currentPackageMonthlyPrice: (state: any, getters: any) => getters.currentPackage?.monthly_price || 0,
    // features
    currentAddons: (state: any) => (state.subscription.features ?? []).filter((feature: FeatureModel) => feature.type === 'addon'),
    packageFeatures: (state: any) => (state.subscription.features ?? []).filter((feature: FeatureModel) => feature.type === 'package'),
    isAddonPurchased: (state: any, getters: any, rootState: any, rootGetters: any) => (addonSlug: string) => !!(getters.currentAddons.find((addon: any) => (!addon.branch_id || addon.branch_id === rootGetters['branches/currentBranchId']) && !addon.expired && addonSlug === addon.slug)),
    hasInventory: (state: any) => state.packagePermissions?.inventory ?? false,
    // limits
    branchesLimit: (state: any) => state.subscription.branches_limit || Infinity,
    devicesLimit: (state: any) => state.subscription.devices_limit || Infinity,
    languagesLimit: (state: any) => state.subscription.languages || Infinity,
    itemsLimit: (state: any) => state.subscription.qr_limit || Infinity,
    subUsersLimit: (state: any) => state.subscription.subusers_limit || Infinity,
    // addons
    isBrandedDomainPurchased: (state: any, getters: any, rootState: any, rootGetters: any) => rootGetters['branches/currentBranch'].branded_domain ?? false,
    isOnlinePaymentPurchased: (state: any) => state.subscription.online_payment ?? false,
    isGoogleViralPurchased: (state: any, getters: any, rootState: any, rootGetters: any) => rootGetters['branches/currentBranch'].google ?? false,
    isMobileAppPurchased: (state: any, getters: any) => getters.isAddonPurchased('mobile-app') ?? false,
    isWhatsappMarketingPurchased: (state: any, getters: any) => getters.isAddonPurchased('wp_marketing') ?? false,
    isPaymentLinksPurchased: (state: any, getters: any) => getters.isAddonPurchased('payment_links') ?? false,

    // expiration
    canRenew: (state: any) => state.subscription.can_renew,
    canExtendTrial: (state: any) => state.subscription.can_extend_trial,
    canGetExtraFree: (state: any) => state.subscription.can_get_extra_free,
    expirationDate: (state: any) => state.subscription.expires_at,
    isExpired: (state: any) => dayjs(state.subscription.expires_at).startOf('day').isBefore(dayjs()),
    expiresAt: (state: any) => state.subscription.expires_at,
    expiresIn: (state: any) => dayjs(state.subscription.expires_at).diff(dayjs().startOf('day'), 'day'),
    expiresSoon: (state: any, getters: any) => getters.expiresIn <= 3 && !getters.isExpired, // before 3 days we should notify user to renew
    // Grace
    inGrace: (state: any) => state.subscription.in_grace,
    gracePeriod: (state: any) => state.subscription.grace_period,
    graceType: (state: any) => state.subscription.grace_type,
    // Prices
    price: (state: any) => state.subscription.price,
    totalPrice: (state: any) => state.subscription.total_price,
  },
  mutations: {
    SET_SUBSCRIPTION(state: any, subscription: any) {
      state.packagePermissions = subscription.package_permissions ?? {}
      const sub = { ...subscription }
      delete sub.package_permissions
      state.subscription = {
        ...state.subscription,
        ...sub,
      }
      state.detailsFetched = true
    },
  },
  actions: {
    async FETCH_SUBSCRIPTION_DETAILS(context: any, force = false) {
      if (!force && context.state.detailsFetched) return context.state.subscription

      const subscription = await SubscriptionAPIs.getMySubscriptionDetails()
      context.commit('SET_SUBSCRIPTION', subscription)
      return subscription
    },
  },
}
